import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
	Button,
	Calendar,
	Chip,
	DrawerBodyWrapper,
	Input,
	ItemCard,
	PageLevelTableWrapper,
	SectionHeader,
	Select,
	Table,
	TableActionButton,
	TableActionButtonWrapper,
	Tabs,
	Textarea,
	TOAST_TYPE,
} from '@base'
import { DISPATCH_PLAN_STATUS, DISPATCH_PLAN_VEHICLE_STATUS, MODAL_TYPES, strings } from '@constants'
import { getDispatchPlanById } from '@data/conn/get/dispatchPlan'
import { showLoader, showToast } from '@data/state/action'
import { ListItemWrapper, ListWrapper } from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/PaymentTerms'
import { capitalizeWord, displayAmount, displayWeight, showDateFormat } from '@utils'
import { ButtonsWrapper, CopySpan, PaymentListWrapper, TabsWrapper } from '../style'
import { useForceUpdate } from '@hooks'
import {
	addDispatchInvoice,
	addVehicleDriverDetails,
	deleteDispatchPlanInvoice,
	updateDisaptchInvoiceWeight,
	updateDispatchPlanStartDate,
	updateDispatchPlanStatus,
	updateVehicleDriverDetailsStatus,
} from '@data'
import { BiInputWrapper } from '@pages/Admin/Lead/AddLead/AddLead'
import { Check, Delete, Edit } from '@material-ui/icons'
import { toggleModal } from '@data/state/action/root'
import { isSuperAdmin } from 'src/utils/adminFilter'

const displayName = (user) => {
	return user?.organisation_name ?? user?.full_name ?? user?.organisation_id
}

const dispatchPlanStatusTypes = Object.entries(DISPATCH_PLAN_STATUS)
	.map(([key, value]) => ({ key, label: value }))
	.filter((item) =>
		[
			DISPATCH_PLAN_STATUS.COMPLETED,
			DISPATCH_PLAN_STATUS.CLOSED,
			DISPATCH_PLAN_STATUS.CANCELLED,
			DISPATCH_PLAN_STATUS.RESCHEDULED,
			DISPATCH_PLAN_STATUS.DELETED,
		].includes(item.key)
	)

const vehicleStatusTypes = Object.entries(DISPATCH_PLAN_VEHICLE_STATUS).map(([key, value]) => ({ key, label: value }))

const deriveFormState = (data) => {
	const state = {
		new_remark: {
			id: 'remark',
			label: strings('remarks'),
			placeholder: strings('enter', 'remarks'),
			value: '',
			disabled: false,
			required: false,
		},
		status: {
			id: 'status',
			label: strings('status'),
			placeholder: strings('select', 'status'),
			value: '',
			primary_key: 'key',
			display_key: 'label',
			data: dispatchPlanStatusTypes,
			disabled: false,
			required: false,
		},
		rescheduling_date: {
			id: 'rescheduling-date',
			label: strings('rescheduling_date'),
			value: new Date(),
			disabled: false,
			required: false,
		},
	}
	if (data) {
	}
	return state
}

const deriveVehicleFormState = (data, edit) => {
	const state = {
		driver_number: {
			id: 'driver-number',
			value: '',
			label: strings('driver', 'number'),
			placeholder: strings(['enter', 'driver', 'number']),
			disabled: false,
			required: false,
		},
		vehicle_number: {
			id: 'vehicle-number',
			placeholder: strings(['enter', 'vehicle', 'number']),
			label: strings('vehicle', 'number'),
			value: '',
			valid: true,
			disabled: false,
			required: false,
		},
		vehicle_status: {
			id: 'vehicle-status',
			label: strings('vehicle', 'status'),
			placeholder: strings(['enter', 'vehicle', 'status']),
			value: '',
			valid: true,
			disabled: false,
			required: false,
		},
		dispatch_date: {
			id: 'dispatch-date',
			label: strings('dispatch', 'date'),
			value: new Date(),
			disabled: false,
			required: false,
		},
	}
	if (data && edit) {
		state.dispatch_date.value = data?.dispatch_date
		state.dispatch_date.disabled = true
		state.driver_number.value = data?.driver_number
		state.driver_number.disabled = true
		state.vehicle_number.value = data?.vehicle_number
		state.vehicle_number.disabled = true
		state.vehicle_status.value = data?.vehicle_status
	}
	return state
}

const DispatchPlanDrawer = ({ dispatchPlanId }) => {
	const dispatch = useDispatch()
	const [forceUpdate, update] = useForceUpdate()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))

	const [selectedTab, setSelectedTab] = useState(0)
	const [dispatchPlanData, setDispatchPlanData] = useState()
	const [formState, setFormState] = useState(deriveFormState())
	const [newInvoiceNumber, setNewInvoiceNumber] = useState('')
	const [openInvoiceForm, setOpenInvoiceForm] = useState(false)
	const [openVehicleDetailsForm, setOpenVehicleDetailsForm] = useState(false)
	const [editInvoiceNumber, setEditInvoiceNumber] = useState(-1)
	const [weight, setWeight] = useState('')

	const [vehicleFormState, setVehicleFormState] = useState(deriveVehicleFormState())

	useEffect(() => {
		dispatch(showLoader(true))
		getDispatchPlanById(dispatchPlanId)
			.then((response) => {
				setDispatchPlanData(response)
				setFormState(deriveFormState(response))
			})
			.catch((error) => {})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [dispatchPlanId, forceUpdate])

	const tabsData = useMemo(() => {
		return [
			{
				label: `${strings('invoice', 'list')} (${dispatchPlanData?.invoice_list?.length ?? 0})`,
				selected: selectedTab === 0,
				onClick: () => setSelectedTab(0),
			},
			{
				label: `${strings('vehicle', 'details')} (${dispatchPlanData?.vehicle_details?.length ?? 0})`,
				selected: selectedTab === 1,
				onClick: () => setSelectedTab(1),
			},
			{ label: strings('edit'), selected: selectedTab === 2, onClick: () => setSelectedTab(2) },
		]
	}, [selectedTab, dispatchPlanData])

	const editInvoiceItem = (data) => {
		setWeight(data?.serviced_weight / 1000)
		setEditInvoiceNumber(data?.invoice_number)
	}

	const deleteInvoice = (data) => {
		dispatch(showLoader(true))
		deleteDispatchPlanInvoice(dispatchPlanId, data?.invoice_number, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_invoice_deleted_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.data?.message ?? strings('msg_invoice_deleted_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const deleteHandler = (data) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('msg_want_to_delete_invoice'),
				confirmAction: (actionType) => {
					actionType && deleteInvoice(data)
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const updateInvoiceWeight = () => {
		if (weight === 0 || weight === '') {
			dispatch(showToast(true, strings('msg_invoice_weight_invalid'), { type: TOAST_TYPE.ERROR }))
		}
		dispatch(showLoader(true))
		updateDisaptchInvoiceWeight(dispatchPlanId, editInvoiceNumber, weight * 1000, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_invoice_updated_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.data?.message ?? strings('msg_invoice_updated_failed')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				setEditInvoiceNumber(-1)
				update()
				dispatch(showLoader(false))
			})
	}

	const invoiceListColumns = useMemo(() => {
		return [
			{ label: strings('original_invoice_number'), key: 'original_invoice_number', mobile: true },
			{
				label: strings(strings('invoice', 'date')),
				key: 'invoice_date',
				renderer: (data) => {
					return showDateFormat(new Date(data))
				},
			},
			{
				label: strings('serviced_weight'),
				key: (data) => data,
				renderer: (data) => {
					if (editInvoiceNumber === data?.invoice_number) {
						return <Input type='number' small value={weight ?? ''} onChange={setWeight} />
					}
					return displayWeight(data?.serviced_weight / 1000)
				},
			},
			{
				label: strings('actions'),
				key: (data) => data,
				renderer: (data) => {
					return (
						<TableActionButtonWrapper>
							{editInvoiceNumber === data?.invoice_number ? (
								<TableActionButton type='primary' small uppercase onClick={updateInvoiceWeight}>
									{<Check fontSize='small' />}
								</TableActionButton>
							) : (
								<TableActionButton type='primary' small uppercase onClick={() => editInvoiceItem(data)}>
									{<Edit fontSize='small' />}
								</TableActionButton>
							)}
							<TableActionButton type='secondary' variant='destructive' small uppercase onClick={() => deleteHandler(data)}>
								{<Delete fontSize='small' />}
							</TableActionButton>
						</TableActionButtonWrapper>
					)
				},
			},
		]
	}, [dispatchPlanId, editInvoiceNumber, weight])

	const updateVehicleStatus = () => {
		const obj = {
			vehicle_number: vehicleFormState?.vehicle_number?.value,
			driver_number: vehicleFormState?.driver_number?.value,
			vehicle_status: vehicleFormState.vehicle_status?.value,
			dispatch_date: new Date(vehicleFormState?.dispatch_date?.value).getTime(),
		}
		dispatch(showLoader(true))
		updateVehicleDriverDetailsStatus(dispatchPlanId, obj, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_vehicle_status_updated_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_vehicle_status_updated_fail')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
				setOpenVehicleDetailsForm(false)
				setVehicleFormState(deriveVehicleFormState())
			})
	}

	const editVehicleDriverDetail = (data) => {
		setOpenVehicleDetailsForm(true)
		setVehicleFormState(deriveVehicleFormState(data, true))
	}

	const VehicleColumsn = useMemo(() => {
		return [
			{ label: strings('vehicle', 'number'), key: 'vehicle_number', mobile: true, align: 'left' },
			{ label: strings('driver', 'number'), key: 'driver_number', mobile: true, align: 'left' },
			{
				label: strings('status'),
				key: 'vehicle_status',
				mobile: true,
			},
			{
				label: strings('actions'),
				key: (data) => data,
				renderer: (data) => (
					<TableActionButtonWrapper>
						<TableActionButton type='primary' small uppercase onClick={() => editVehicleDriverDetail(data)}>
							{<Edit fontSize='small' />}
						</TableActionButton>
					</TableActionButtonWrapper>
				),
				mobile: true,
			},
		]
	}, [])

	const copyDispatchPlanId = (e, id) => {
		if (!id) {
			return
		}
		e.stopPropagation()
		navigator.clipboard.writeText(id)
		dispatch(
			showToast(true, strings(['dispatch_plan', 'id', 'copied']), {
				type: TOAST_TYPE.INFO,
			})
		)
	}

	const updateState = (key) => (value) => {
		setFormState((_state) => ({ ..._state, [key]: { ..._state[key], value: value } }))
	}

	const updateVehicleFormState =
		(key, type = 'value') =>
		(value) => {
			if (type === 'valid') {
				setVehicleFormState((_state) => ({ ..._state, [key]: { ..._state[key], valid: value } }))
			} else {
				setVehicleFormState((_state) => ({ ..._state, [key]: { ..._state[key], value: value } }))
			}
		}

	const addNewInvoice = () => {
		dispatch(showLoader(true))
		addDispatchInvoice(dispatchPlanId, dispatchPlanData?.order_id, newInvoiceNumber, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_invoice_attached_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.data?.message ?? strings('msg_invoice_attach_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				setNewInvoiceNumber('')
				setOpenInvoiceForm(false)
				dispatch(showLoader(false))
			})
	}
	const getStatusBg = (key) => {
		switch (key) {
			case DISPATCH_PLAN_STATUS.COMPLETED:
				return 'green'
			case DISPATCH_PLAN_STATUS.CANCELLED:
			case DISPATCH_PLAN_STATUS.DELETED:
				return '#c21625'
			case DISPATCH_PLAN_STATUS.ACTIVE:
				return '#1967D2'
			case DISPATCH_PLAN_STATUS.SCHEDULED:
				return '#ffa07a'
			case DISPATCH_PLAN_STATUS.RESCHEDULED:
				return '#e69500'
			case DISPATCH_PLAN_STATUS.CLOSED:
				return '#6b7280'
			default:
				return '#323234'
		}
	}

	const renderBasicDetails = () => {
		const itemData = [
			{ label: strings('enquiry_id'), value: dispatchPlanData?.enquiry_id ?? '-' },
			{ label: strings('order_id'), value: dispatchPlanData?.order_id ?? '-' },
			{
				label: strings('buyer'),
				value: displayName(dispatchPlanData?.buyer) || '-',
				wrap: true,
			},
			{
				label: strings('supplier'),
				value: displayName(dispatchPlanData?.supplier) || '-',
				wrap: true,
			},
			{ label: strings(['buyer', 'po', 'number']), value: dispatchPlanData?.original_buyer_po_number },
			{ label: strings(['supplier', 'po', 'number']), value: dispatchPlanData?.original_supplier_po_number },
			{
				label: strings('start', 'date'),
				value: showDateFormat(new Date(dispatchPlanData?.plan_start_date)) || '-',
			},
			{
				label: strings('current', 'date'),
				value: showDateFormat(new Date(dispatchPlanData?.plan_current_date)) || '-',
				wrap: true,
			},
			{
				label: strings('status'),
				value: (
					<Chip
						label={dispatchPlanData?.status}
						status={'custom'}
						background={() => getStatusBg(dispatchPlanData?.status)}
						color={'white'}
					/>
				),
			},
			{ label: strings('logistics_handler'), value: dispatchPlanData?.logistics_handler ?? '-' },
			{ label: strings('price_per_kg'), value: displayAmount(dispatchPlanData?.price_per_kg) },
			{
				label: strings('planned', 'quantity'),
				value: displayWeight(dispatchPlanData?.dispatch_quantity / 1000),
			},
			{
				label: strings('dispatched', 'quantity'),
				value: displayWeight(dispatchPlanData?.serviced_quantity / 1000),
			},
			{
				label: strings('age'),
				value: dispatchPlanData?.age,
			},
			{
				label: strings('planned'),
				value: dispatchPlanData?.planned ? 'YES' : 'NO',
			},
		]
		return (
			<>
				<SectionHeader>
					<div>{`Basic Details`}</div>
					<CopySpan onClick={(e) => copyDispatchPlanId(e, dispatchPlanData?.dispatch_plan_id)}>
						{dispatchPlanData?.dispatch_plan_id}
					</CopySpan>
				</SectionHeader>
				<ItemCard data={itemData} key={`plan-item`} medium />
			</>
		)
	}

	const renderInvoiceList = () => {
		return (
			<>
				{!openInvoiceForm && dispatchPlanData?.invoice_list?.length > 0 && (
					<PageLevelTableWrapper>
						<Table
							data={dispatchPlanData?.invoice_list}
							columns={invoiceListColumns}
							rowsToDisplay={dispatchPlanData?.invoice_list?.length}
							totalItemCount={dispatchPlanData?.invoice_list?.length}
							page={0}
							rowClickable={false}
							hideFooter
							// fullHeight
							noScroll
						/>
					</PageLevelTableWrapper>
				)}
				{openInvoiceForm && (
					<>
						<SectionHeader>{strings('attach_invoice')}</SectionHeader>
						<Input
							id='attach-invoice'
							label={strings('original_invoice_number')}
							placeholder={strings('enter', 'original_invoice_number')}
							value={newInvoiceNumber}
							onChange={setNewInvoiceNumber}
						/>
						<ButtonsWrapper>
							<Button xs variant='destructive' type='secondary' onClick={() => setOpenInvoiceForm(false)}>
								{strings('cancel')}
							</Button>
							<Button xs disabled={!newInvoiceNumber} onClick={addNewInvoice}>
								{strings('attach')}
							</Button>
						</ButtonsWrapper>
					</>
				)}
			</>
		)
	}

	const renderPaymentTerms = () => {
		if (!dispatchPlanData?.payment_terms_list || dispatchPlanData?.payment_terms_list?.length === 0) {
			return
		}
		return (
			<PaymentListWrapper>
				<ListWrapper>
					<>{strings('payment_terms')}</>
					{dispatchPlanData?.payment_terms_list?.map((item, index) => (
						<ListItemWrapper key={`term-${index}`}>
							<div>{`${item?.percentage}% ${capitalizeWord(item?.payment_term ?? '')} ${
								item?.condition
									? item?.condition
											?.split('_')
											?.map((item) => capitalizeWord(item ?? ''))
											?.join(' ')
									: ''
							} for ${item?.tenure_in_days} days`}</div>
						</ListItemWrapper>
					))}
				</ListWrapper>
			</PaymentListWrapper>
		)
	}
	const updateStatus = () => {
		const getRemarkAction = () => {
			const key = formState?.status?.value
			switch (key) {
				case DISPATCH_PLAN_STATUS.COMPLETED:
					return 'DISPATCH_PLAN_COMPLETED'
				case DISPATCH_PLAN_STATUS.CANCELLED:
					return 'DISPATCH_PLAN_CANCELLED'
				case DISPATCH_PLAN_STATUS.DELETED:
					return 'DISPATCH_PLAN_DELETED'
				case DISPATCH_PLAN_STATUS.RESCHEDULED:
					return 'DISPATCH_PLAN_RESCHEDULED'
				case DISPATCH_PLAN_STATUS.CLOSED:
					return 'DISPATCH_PLAN_CLOSED'
				default:
					return ''
			}
		}
		const obj = {
			created_at: new Date().getTime(),
			remark_by: loggedInUser?.user_id,
			remark: formState?.new_remark?.value,
			action: getRemarkAction(),
		}
		dispatch(showLoader(true))
		updateDispatchPlanStatus(
			dispatchPlanId,
			obj,
			formState?.status?.value,
			formState?.status?.value === DISPATCH_PLAN_STATUS.RESCHEDULED
				? new Date(formState?.rescheduling_date?.value).getTime()
				: new Date().getTime(),
			loggedInUser?.user_id
		)
			.then((response) => {
				dispatch(showToast(true, strings('msg_dispatch_plan_status_updated'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.response?.data?.message ?? strings('msg_dispatch_plan_status_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const disableUpdateStatus = useMemo(() => {
		if (formState?.status?.value === DISPATCH_PLAN_STATUS.RESCHEDULED) {
			return Boolean(!formState?.rescheduling_date?.value || !formState?.new_remark?.value)
		}
		return Boolean(!formState?.status?.value)
	}, [formState])

	const submitVehicleDetails = () => {
		const obj = {
			vehicle_number: vehicleFormState?.vehicle_number?.value,
			driver_number: vehicleFormState?.driver_number?.value,
			vehicle_status: vehicleFormState?.vehicle_status?.value,
			dispatch_date: new Date(vehicleFormState?.dispatch_date?.value).getTime(),
		}
		dispatch(showLoader(true))
		addVehicleDriverDetails(dispatchPlanId, obj, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_vehicle_added_in_dispatch_plan'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.data?.message ?? strings('msg_vehicle_added_in_dispatch_plan_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				setVehicleFormState(deriveVehicleFormState())
				setOpenVehicleDetailsForm(false)
				dispatch(showLoader(false))
			})
	}

	const vehicleDetailsDisabled = useMemo(() => {
		return Boolean(!vehicleFormState?.vehicle_number?.value || !vehicleFormState?.driver_number?.value || !vehicleFormState?.driver_number?.valid)
	}, [vehicleFormState])

	const renderVehicleDetails = () => {
		return (
			<>
				{!openVehicleDetailsForm ? (
					dispatchPlanData?.vehicle_details?.length > 0 && (
						<>
							<PageLevelTableWrapper>
								<Table
									data={dispatchPlanData?.vehicle_details}
									columns={VehicleColumsn}
									rowsToDisplay={dispatchPlanData?.vehicle_details.length}
									totalItemCount={dispatchPlanData?.vehicle_details?.length}
									page={0}
									rowClickable={false}
									hideFooter
									fullHeight
									// noScroll
								/>
							</PageLevelTableWrapper>
						</>
					)
				) : (
					<>
						<BiInputWrapper>
							<Input
								id={vehicleFormState?.vehicle_number?.id}
								label={vehicleFormState?.vehicle_number?.label}
								placeholder={vehicleFormState?.vehicle_number?.placeholder}
								value={vehicleFormState?.vehicle_number?.value}
								disabled={vehicleFormState?.vehicle_number?.disabled}
								required={vehicleFormState?.vehicle_number?.required}
								onChange={updateVehicleFormState('vehicle_number')}
							/>
							<Input
								id={vehicleFormState?.driver_number?.id}
								label={vehicleFormState?.driver_number?.label}
								placeholder={vehicleFormState?.driver_number?.placeholder}
								value={vehicleFormState?.driver_number?.value}
								pattern='[789][0-9]{9}'
								errorMap={{
									patternMismatch: `${strings('enter_valid_num')}`,
									valueMissing: `${strings('mob_num_required')}`,
								}}
								setValidity={updateVehicleFormState('driver_number', 'valid')}
								maxLength={10}
								disabled={vehicleFormState?.driver_number?.disabled}
								required={vehicleFormState?.driver_number?.required}
								onChange={updateVehicleFormState('driver_number')}
							/>
						</BiInputWrapper>
						<BiInputWrapper>
							<Select
								id={vehicleFormState?.vehicle_status?.id}
								label={vehicleFormState?.vehicle_status?.label}
								placeholder={vehicleFormState?.vehicle_status?.placeholder}
								data={vehicleStatusTypes}
								primaryKey='key'
								displayKey='label'
								value={vehicleStatusTypes?.find((item) => item.key === vehicleFormState?.vehicle_status?.value) ?? {}}
								disabled={vehicleFormState?.vehicle_status?.disabled}
								required={vehicleFormState?.vehicle_status?.required}
								onChange={(val) => updateVehicleFormState('vehicle_status')(val.key)}
							/>
							<Calendar
								id={vehicleFormState?.dispatch_date?.id}
								label={vehicleFormState?.dispatch_date?.label}
								placeholder={vehicleFormState?.dispatch_date?.placeholder}
								value={vehicleFormState?.dispatch_date?.value}
								onChange={updateVehicleFormState('dispatch_date')}
								disabled={vehicleFormState?.dispatch_date?.disabled}
								required={vehicleFormState?.dispatch_date?.required}
							/>
						</BiInputWrapper>
						<ButtonsWrapper>
							<Button xs variant='destructive' type='secondary' onClick={() => setOpenVehicleDetailsForm(false)}>
								{strings('cancel')}
							</Button>
							{vehicleFormState?.vehicle_number?.disabled ? (
								<Button xs onClick={updateVehicleStatus}>
									{strings('edit')}
								</Button>
							) : (
								<Button xs onClick={submitVehicleDetails} disabled={vehicleDetailsDisabled}>
									{strings('add')}
								</Button>
							)}
						</ButtonsWrapper>
					</>
				)}
			</>
		)
	}
	const updatePlanStartDate = (val) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('msg_sure_want_to_change_plan_start_date'),
				confirmAction: (actionType) => {
					if (actionType) {
						dispatch(showLoader(true))
						updateDispatchPlanStartDate(dispatchPlanId, val, loggedInUser?.user_id)
							.then((response) => {
								dispatch(showToast(true, strings('msg_plan_start_date_update_success'), { type: TOAST_TYPE.SUCCESS }))
							})
							.catch((error) => {
								let error_msg = error?.message ?? strings('msg_plan_start_date_update_failure')
								dispatch(showToast(true, error_msg), { type: TOAST_TYPE.ERROR })
							})
							.finally(() => {
								update()
								dispatch(toggleModal(false))
								dispatch(showLoader(false))
							})
					}
				},
			})
		)
	}
	const renderEditables = () => {
		return (
			<>
				<SectionHeader>{}</SectionHeader>
				<Select
					id={formState?.status?.id}
					label={formState?.status?.label}
					placeholder={formState?.status?.placeholder}
					data={formState?.status?.data}
					value={dispatchPlanStatusTypes.find((item) => item.key === formState?.status?.value) ?? {}}
					primaryKey={formState?.status?.primary_key}
					displayKey={formState?.status?.display_key}
					disabled={formState?.status?.disabled}
					required={formState?.status?.required}
					onChange={(value) => updateState('status')(value.key)}
				/>
				{Boolean(formState?.status?.value === DISPATCH_PLAN_STATUS.RESCHEDULED) && (
					<Calendar
						id={formState?.rescheduling_date?.id}
						label={formState?.rescheduling_date?.label}
						placeholder={formState?.rescheduling_date?.placeholder}
						value={formState?.rescheduling_date?.value}
						onChange={updateState('rescheduling_date')}
						// openBelow={false}
						position={'top'}
					/>
				)}
				<Textarea
					label={formState?.new_remark?.label}
					placeholder={formState?.new_remark?.placeholder}
					value={formState?.new_remark?.value}
					onChange={updateState('new_remark')}
					disabled={formState?.new_remark?.disabled}
					required={formState?.new_remark?.required}
				/>
				<ButtonsWrapper>
					<Button xs disabled={disableUpdateStatus} onClick={updateStatus}>
						{strings('update', 'status')}
					</Button>
				</ButtonsWrapper>
				{isSuperAdmin(loggedInUser?.role) && (
					<>
						<SectionHeader></SectionHeader>
						<Calendar
							label={strings('plan_start_date')}
							value={dispatchPlanData?.plan_start_date}
							onChange={updatePlanStartDate}
							position={'top'}
						/>
					</>
				)}
			</>
		)
	}

	const tabularData = () => {
		return (
			<TabsWrapper>
				<Tabs data={tabsData} />
				{selectedTab === 0 && !openInvoiceForm && (
					<Button xs type='secondary' onClick={() => setOpenInvoiceForm(true)}>
						{strings('add')}
					</Button>
				)}
				{selectedTab === 1 && !openVehicleDetailsForm && (
					<Button xs type='secondary' onClick={() => setOpenVehicleDetailsForm(true)}>
						{strings('add')}
					</Button>
				)}
			</TabsWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				{renderBasicDetails()}
				{renderPaymentTerms()}
				{tabularData()}
				{selectedTab === 0 && renderInvoiceList()}
				{selectedTab === 1 && renderVehicleDetails()}
				{selectedTab === 2 && renderEditables()}
			</DrawerBodyWrapper>
		</>
	)
}

export default DispatchPlanDrawer
